import { api, type ApiError } from "./client";
import { useResource } from "./useResource";

export type MemberRole = "admin" | "member" | "owner";

export type ApiWorkspace = {
  readonly created_at: string;
  readonly name: string;
  readonly public_id: string;
  readonly role?: MemberRole;
  readonly slug: string;
  readonly status: string;
};

export type ApiMember = {
  readonly accepted_at: string | null;
  readonly avatar_initials: string;
  readonly display_name: string;
  readonly email: string;
  readonly public_id: string;
  readonly role: MemberRole;
};

// Which workspace the caller is acting in lives in `./currentWorkspace`, which
// is the only reader of `GET /workspaces`. Everything below is a call *about* a
// workspace and takes the id from there.

// Workspace runtime defaults, server-persisted (settings.md §Runtime defaults).
export type WorkspaceSettings = {
  readonly auto_open_pr: boolean;
  readonly default_effort: "high" | "low" | "max" | "medium" | "xhigh";
  readonly default_model: string;
  readonly pause_on_failing_tests: boolean;
  readonly retention_done_days: number;
};

export type UseWorkspaceSettings = {
  readonly refetch: () => void;
  readonly settings: WorkspaceSettings | null;
};

export const useWorkspaceSettings = (
  workspaceId: string | null,
): UseWorkspaceSettings => {
  const { data, refetch } = useResource<WorkspaceSettings | null>(
    workspaceId ? `/workspaces/${workspaceId}/settings` : null,
  );
  return { refetch, settings: data ?? null };
};

export const updateWorkspaceSettings = (
  workspaceId: string,
  patch: Partial<WorkspaceSettings>,
): Promise<unknown> => api.patch(`/workspaces/${workspaceId}/settings`, patch);

export type UseMembers = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly members: ReadonlyArray<ApiMember>;
  readonly refetch: () => void;
};

export const useMembers = (workspaceId: string | null): UseMembers => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiMember>
  >(workspaceId ? `/workspaces/${workspaceId}/members` : null);
  return { error, loading, members: data ?? [], refetch };
};

// The invite response returns the join `token` — the server emails the join
// link, and the inviter can also copy it directly.
export const inviteMember = (
  workspaceId: string,
  input: { email: string; role: "admin" | "member" },
): Promise<{
  invitation: { email: string; public_id: string; role: string; token: string };
}> => api.post(`/workspaces/${workspaceId}/members/invite`, input);

// Public preview of an invitation by token (powers the /join page + sign-in
// banner). No session required — the invitee may not have an account yet.
export type InvitationPreview = {
  readonly display_name: string | null;
  readonly email: string;
  readonly expired: boolean;
  // Whether the account already has a passkey — the page offers "sign in with
  // your passkey" vs "create your passkey" (enroll) accordingly.
  readonly has_passkey: boolean;
  readonly inviter_name: string | null;
  readonly role: string | null;
  readonly status: string;
  readonly workspace_name: string | null;
};

export const previewInvitation = (token: string): Promise<InvitationPreview> =>
  api.get(`/invitations/${encodeURIComponent(token)}`);

// Accept an invitation. The signed-in user joins the workspace at the invited
// role; the token is the credential.
export const acceptInvitation = (
  token: string,
): Promise<{ workspace: { public_id: string } }> =>
  api.post("/invitations/accept", { token });

export type ApiInvitation = {
  readonly created_at: string;
  readonly email: string;
  readonly expires_at: string;
  readonly public_id: string;
  readonly role: string;
  readonly status: string;
};

export type UseInvitations = {
  readonly invitations: ReadonlyArray<ApiInvitation>;
  readonly refetch: () => void;
};

export const useInvitations = (workspaceId: string | null): UseInvitations => {
  const { data, refetch } = useResource<ReadonlyArray<ApiInvitation>>(
    workspaceId ? `/workspaces/${workspaceId}/invitations` : null,
  );
  return { invitations: data ?? [], refetch };
};

export const revokeInvitation = (
  workspaceId: string,
  invitePublicId: string,
): Promise<unknown> =>
  api.del(`/workspaces/${workspaceId}/invitations/${invitePublicId}`);

export const changeMemberRole = (
  workspaceId: string,
  userPublicId: string,
  role: MemberRole,
): Promise<unknown> =>
  api.patch(`/workspaces/${workspaceId}/members/${userPublicId}`, { role });

export const removeMember = (
  workspaceId: string,
  userPublicId: string,
): Promise<unknown> =>
  api.del(`/workspaces/${workspaceId}/members/${userPublicId}`);

// GET /api/v1/audit-events — the workspace activity log (admin; api.md §Audit).
export type ApiAuditEvent = {
  readonly action: string;
  readonly actor_initials: string | null;
  readonly actor_kind: string;
  readonly actor_name: string | null;
  readonly meta: Record<string, unknown> | null;
  readonly object_kind: string | null;
  readonly occurred_at: string;
  readonly public_id: string;
};

export type UseAuditEvents = {
  readonly error: ApiError | undefined;
  readonly events: ReadonlyArray<ApiAuditEvent>;
  readonly loading: boolean;
};

export const useAuditEvents = (workspaceId: string | null): UseAuditEvents => {
  const { data, error, loading } = useResource<{
    events: ReadonlyArray<ApiAuditEvent>;
  }>(
    workspaceId ? "/audit-events" : null,
    workspaceId ? { workspace_id: workspaceId } : undefined,
  );
  return { error, events: data?.events ?? [], loading };
};
